import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { ROUTES } from "../../../routes/routes.constants";
import AppButton from "../../../common_components/AppButton";
import CommonChip from "../../../common_components/CommonChip";
import CommonTable from "../../../common_components/CommonTable";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import Pagination from "../../../common_components/Pagination";
import TableToolbar from "../../../common_components/TableToolbar";
import { ExportButton } from "../../../common_components/FormInputs";
import { EXPAND_TRIGGERS, SUMMARY_ROW_POSITIONS } from "../../../common_components/tableConstants";
import { NETWORK_METADATA_MAP } from "../../../utils/networkConstants";
import { isDualValue, } from "../billingSummary.types";
import AdjustTotalBillModal from "./AdjustTotalBillModal";
import DevelopmentHoursModal from "./DevelopmentHoursModal";
import GenerateInvoiceModal from "./GenerateInvoiceModal";
import InvoicePreviewModal from "./InvoicePreviewModal";
import MappingInvoiceModal from "./MappingInvoiceModal";
import BillingSummaryDetailsModal from "./BillingSummaryDetailsModal";
import { useNavigate } from "react-router-dom";
import InfoTooltip from "../../../common_components/InfoTooltip";
const ITEMS_PER_PAGE = 10;
const columns = [
    { id: "invoiceNo", label: "# No.", width: "120px", align: "left", sortable: true },
    { id: "client", label: "Client", width: "240px", align: "left", sortable: true },
    { id: "am", label: "AM", width: "90px", align: "center", sortable: false },
    // { id: "internalStatus", label: "Internal Status", width: "150px", align: "left", sortable: true },
    { id: "invoiceStatus", label: "Invoice Status", width: "180px", align: "left", sortable: true },
    { id: "source", label: "Source", width: "130px", align: "center", sortable: false },
    { id: "startDate", label: "Start Date", width: "150px", align: "left", sortable: true },
    { id: "endDate", label: "End Date", width: "150px", align: "left", sortable: true },
    { id: "notes", label: "Notes", width: "300px", align: "left", sortable: true },
    { id: "commDaysInMonth", label: "Days in Month (Commission)", width: "150px", align: "center", sortable: true },
    { id: "commDaysBilled", label: "Days Billed in Month (Commission)", width: "150px", align: "center", sortable: true },
    { id: "mgmtDaysInMonth", label: "Days in Month (Management)", width: "150px", align: "center", sortable: true },
    { id: "mgmtDaysBilled", label: "Days Billed in Month (Management)", width: "150px", align: "center", sortable: true },
    { id: "fullMgmtFee", label: "Full Management Fee", width: "150px", align: "left", sortable: true },
    { id: "billableMgmtFee", label: "Billable Management Fee", width: "150px", align: "left", sortable: true },
    { id: "commissionBaseline", label: "Commission Baseline", width: "150px", align: "left", sortable: true },
    { id: "revenueOrders", label: "Revenue/Orders", width: "170px", align: "left", sortable: true },
    { id: "commissionableAmount", label: "Commissionable Amount", width: "170px", align: "left", sortable: true },
    { id: "commissionRate", label: "Commission Rate", width: "130px", align: "left", sortable: true },
    { id: "commissions", label: "Commissions", width: "140px", align: "left", sortable: true },
    { id: "developmentHours", label: "Development Hours", width: "150px", align: "center", sortable: true },
    { id: "totalBill", label: "Total Bill", width: "160px", align: "left", sortable: true },
    { id: "invoiceAmount", label: "Invoice Amount", width: "130px", align: "left", sortable: true },
    { id: "clientType", label: "Affiliate or Paid Media", width: "130px", align: "left", sortable: true },
    { id: "affiliatePer", label: "Affiliate %", width: "120px", align: "center", sortable: true },
    { id: "paidSocialPer", label: "Paid Social %", width: "100px", align: "center", sortable: true },
    { id: "paidSearchPer", label: "Paid Search %", width: "100px", align: "center", sortable: true },
    { id: "accountManagementPer", label: "Account Management %", width: "140px", align: "center", sortable: true },
    { id: "emailSmsPer", label: "Email/SMS %", width: "120px", align: "center", sortable: true },
    { id: "totalPer", label: "Total %", width: "110px", align: "center", sortable: true },
];
const TABLE_MIN_WIDTH = columns.reduce((total, col) => total + (parseInt(String(col.width), 10) || 0), 40);
const tableContainerSx = {
    border: "1px solid #EAECF0",
    borderRadius: "8px",
    overflow: "hidden",
    "& table": {
        borderCollapse: "separate",
        borderSpacing: 0,
        tableLayout: "fixed",
        minWidth: `${TABLE_MIN_WIDTH}px`,
    },
    "& th, & td": { borderRight: "1px solid #EAECF0", borderBottom: "1px solid #EAECF0" },
    "& tr > :first-of-type": { borderLeft: "1px solid #EAECF0" },
    "& thead tr:first-of-type th": { borderTop: "1px solid #EAECF0" },
    "& thead th": { background: "#F9FAFB", position: "sticky", zIndex: 6 },
    "& tbody tr:last-of-type td": { backgroundColor: "#F9FAFB", fontWeight: 700, color: "#101828" },
    "& tfoot td, & tfoot th": { backgroundColor: "#F9FAFB", fontWeight: 700 },
    "& thead th .billing-header-label": {
        fontFamily: "'Inter', sans-serif",
        fontWeight: 700,
        color: "#252b37",
        lineHeight: "18px",
        whiteSpace: "normal",
        wordBreak: "normal",
        overflowWrap: "normal",
        hyphens: "none",
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 3,
        overflow: "hidden",
        textAlign: "center",
    },
    "& .reusable-table-container .reusable-table th.reusable-table-header-cell": {
        padding: "8px 12px",
        height: "auto",
    },
};
/**
 * The identity columns stay in view while the ~30 metric columns scroll — same
 * `CommonTable` pinning the LTV Segment Report uses. "selection" has to be listed
 * too: the checkbox column sits left of "# No.", so without it the pinned cells
 * would start at offset 0 and cover it. Offsets come from the declared `width`s
 * (40 + 120 + 200), which `table-layout: fixed` honours exactly.
 *
 * Not user-adjustable — `enableColumnActions` is off here, so there's no pin menu.
 */
const PINNED_COLUMNS = { left: ["selection", "invoiceNo", "client"], right: [] };
/** Leaf columns the user may hide — Client stays put as the pinned identity column. */
const HIDEABLE_COLUMN_IDS = [
    "invoiceNo", "am", "internalStatus", "invoiceStatus", "source", "startDate", "endDate", "notes",
    "commDaysInMonth", "commDaysBilled", "mgmtDaysInMonth", "mgmtDaysBilled",
    "fullMgmtFee", "billableMgmtFee",
    "commissionBaseline", "revenueOrders", "commissionableAmount", "commissionRate", "commissions",
    "developmentHours", "totalBill", "invoiceAmount", "clientType",
    "affiliatePer", "paidSocialPer", "paidSearchPer", "accountManagementPer", "emailSmsPer", "totalPer",
];
/**
 * A split column has no single number to order by, so sorting uses the paid-media
 * (`up`) figure — the side listed first — falling back to the affiliate one.
 */
const sortNumber = (value) => isDualValue(value) ? value.up ?? value.down ?? 0 : value ?? 0;
/** Maps a column id to the raw sortable value on a row. */
const SORT_ACCESSORS = {
    client: (r) => r.client.toLowerCase(),
    internalStatus: (r) => r.internalStatus.toLowerCase(),
    invoiceStatus: (r) => r.invoiceStatus.toLowerCase(),
    fullMgmtFee: (r) => sortNumber(r.fullMgmtFee),
    billableMgmtFee: (r) => sortNumber(r.billableMgmtFee),
    commissionBaseline: (r) => sortNumber(r.commissionBaseline),
    revenueOrders: (r) => sortNumber(r.revenueOrders),
    commissionableAmount: (r) => sortNumber(r.commissionableAmount),
    commissions: (r) => r.commissions,
    totalBill: (r) => r.totalBill,
    invoiceAmount: (r) => r.invoiceAmount,
    clientType: (r) => r.clientType.toLowerCase(),
    affiliatePer: (r) => sortNumber(r.affiliatePer),
    paidSocialPer: (r) => sortNumber(r.paidSocialPer),
    paidSearchPer: (r) => sortNumber(r.paidSearchPer),
    accountManagementPer: (r) => sortNumber(r.accountManagementPer),
    emailSmsPer: (r) => sortNumber(r.emailSmsPer),
    totalPer: (r) => sortNumber(r.totalPer),
};
/**
 * "partially_paid" → "Partially Paid". QuickBooks' `synced` means our push landed but the
 * invoice hasn't gone out yet, which reads as "Not Sent" to anyone looking at the column —
 * and it's parenthesised, as in the legacy report, because it describes the absence of a
 * send rather than a state the invoice reached.
 */
const invoiceStatusLabel = (status) => status === "synced"
    ? "Not Sent"
    : status
        .split("_")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
/**
 * The Invoice Status cell: a state icon, then the due-date standing above the status.
 *
 * Green tick for a settled invoice, amber alert for everything still outstanding — the
 * same two-way split the legacy report drew, since what a reader needs from this column is
 * "is this done or does it need chasing", not a distinct colour per status.
 *
 * Paid collapses to a single line: `invoiceStatusMsg` is empty once an invoice is settled,
 * so there's no due-date line left to show.
 */
const InvoiceStatusCell = React.memo(({ row }) => {
    const isPaid = row.invoiceStatus === "paid";
    const tone = isPaid ? "#079455" : "#F79009";
    // Blank entries are dropped rather than rendered as empty rows: an invoice with no due
    // date carries no standing message, which would otherwise leave a gap above the status.
    const lines = [
        row.invoiceStatusMsg,
        invoiceStatusLabel(row.invoiceStatus),
        // Only a partial payment has a meaningful outstanding balance to name.
        row.invoiceStatus === "partially_paid" && row.invoiceDueAmount
            ? `$${row.invoiceDueAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} due`
            : "",
    ].filter(Boolean);
    return (_jsxs(Box, { sx: { display: "flex", alignItems: "flex-start", gap: "6px", px: 1, py: "2px" }, children: [isPaid ? (_jsx(CheckCircle2, { size: 16, fill: tone, color: "#fff", style: { flexShrink: 0, marginTop: 2 } })) : (_jsx(AlertCircle, { size: 16, color: tone, style: { flexShrink: 0, marginTop: 2 } })), _jsx(Box, { sx: { minWidth: 0 }, children: lines.map((line, index) => (_jsx(Typography, { sx: {
                        fontFamily: "Inter",
                        // The first line is the headline — the standing, or the status
                        // itself when there's no standing to report.
                        fontWeight: index === 0 ? 500 : 400,
                        color: index === 0 ? "#101828" : "#475467",
                        fontSize: "13px !important",
                        lineHeight: "18px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }, children: line }, index))) })] }));
});
/** A count cell (the Days columns) — stacked when the API split it. */
const renderCount = (value) => isDualValue(value)
    ? (_jsx(DualStack, { align: "center", up: _jsx(PlainText, { value: value.up ?? "-", noGutter: true }), down: _jsx(PlainText, { value: value.down ?? "-", noGutter: true }) }))
    : _jsx(PlainText, { value: value ?? "-", align: "center" });
/**
 * A currency cell — stacked when the API split it. `dashWhenZero` matches the
 * Commission Baseline column, which reads better empty than as "$ 0.00".
 */
const renderMoney = (value, dashWhenZero = false) => {
    const money = (amount, noGutter) => dashWhenZero && !amount
        ? _jsx(PlainText, { value: "-", noGutter: noGutter })
        : _jsx(CurrencyText, { value: amount, noGutter: noGutter });
    return isDualValue(value)
        ? _jsx(DualStack, { up: money(value.up, true), down: money(value.down, true) })
        : money(value, false);
};
/**
 * A revenue-split percentage cell — stacked when the API split it. A zero share
 * reads as "-" rather than "0%", matching how Commission Baseline treats zero.
 */
const renderPercent = (value) => {
    const percent = (share, noGutter) => !share
        ? _jsx(PlainText, { value: "-", align: "center", noGutter: noGutter })
        : _jsx(PlainText, { value: `${share}%`, align: "center", noGutter: noGutter });
    return isDualValue(value)
        ? _jsx(DualStack, { align: "center", up: percent(value.up, true), down: percent(value.down, true) })
        : percent(value, false);
};
/**
 * A row can only be invoiced when it actually bills something. Zero (or negative)
 * Total Bill rows — no network connected, no billable days — get no checkbox.
 *
 * Nor can one be raised without the QuickBooks Display Name and Email — the pair the
 * "No Client Info" filter lists — so those rows get no checkbox either.
 */
const isRowSelectable = (row) => row.totalBill > 0 && row.quickbookConfigured;
/**
 * `CommonTable` renders a DISABLED checkbox for non-selectable rows; this removes it
 * from view entirely. The cell itself stays, so column alignment is untouched.
 */
const hideCheckboxWhenNotBillable = (row) => isRowSelectable(row)
    ? undefined
    : { "& .reusable-table-cell-selection .table-checkbox": { display: "none" } };
/** Currency text with a muted "$" symbol, matching the reference tables. */
const CurrencyText = React.memo(({ value, bold, noGutter }) => (_jsx(Box, { sx: { px: noGutter ? 0 : 1, textAlign: "left" }, children: _jsxs(Typography, { sx: {
            fontFamily: "Inter",
            fontWeight: bold ? 600 : 400,
            color: bold ? "#181d27" : "#27272a",
            whiteSpace: "nowrap",
            fontSize: "14px !important",
            // Under `table-layout: fixed` an over-long value spills into the next
            // cell instead of widening its own column, so clip it.
            overflow: "hidden",
            textOverflow: "ellipsis",
        }, children: [_jsx("span", { style: { color: bold ? "#a4a7ae" : "#a1a1aa" }, children: "$ " }), value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })] }) })));
/** Plain cell text — the default treatment for counts, dates and statuses. */
const PlainText = React.memo(({ value, align = "left", bold, noGutter }) => (_jsx(Box, { sx: { px: noGutter ? 0 : 1, textAlign: align }, children: _jsx(Typography, { sx: {
            fontFamily: "Inter",
            fontWeight: bold ? 600 : 400,
            color: bold ? "#181d27" : "#27272a",
            fontSize: "14px !important",
            whiteSpace: "nowrap",
            // See `CurrencyText` — `fixed` layout clips rather than widens.
            overflow: "hidden",
            textOverflow: "ellipsis",
            textTransform: "capitalize"
        }, children: value }) })));
/**
 * The Source column's network icons — same treatment as Account Performance's MC
 * column. Split out because a `both` client renders it twice (paid media / affiliate).
 */
const renderSourceIcons = (channels) => {
    if (channels.length === 0) {
        return (_jsx(Box, { sx: {
                width: 34, height: 34, borderRadius: "50%", backgroundColor: "#f4f4f5",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "13px !important", fontWeight: 600, color: "#71717a",
            }, children: "Na" }));
    }
    return (_jsx(Box, { sx: { display: "flex", gap: 0.5 }, children: channels.map((channel, index) => {
            const meta = NETWORK_METADATA_MAP[channel];
            const label = meta?.name || channel.charAt(0).toUpperCase() + channel.slice(1);
            return (_jsx(HoverTooltip, { title: label, arrow: true, children: _jsx(Box, { sx: { display: "flex" }, children: meta?.icon ? (_jsx("img", { src: meta.icon, alt: label, style: { width: 24, height: 24, objectFit: "contain" } })) : (_jsx(Box, { sx: {
                            width: 30, height: 30, borderRadius: "50%", backgroundColor: "#f4f4f5",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: "13px !important", fontWeight: 600, color: "#71717a",
                        }, children: label == 'Tab' ? label.slice(0, 3) : label.slice(0, 2) })) }) }, `${channel}-${index}`));
        }) }));
};
const DualStack = ({ up, down, align = "left" }) => (_jsxs(Box, { sx: { px: 1, display: "flex", flexDirection: "column", alignItems: align === "center" ? "center" : "flex-start" }, children: [_jsx(Box, { sx: { display: "flex", alignItems: "center", gap: "3px" }, children: up }), _jsx(Box, { sx: { width: "100%", height: "1px", bgcolor: "#F2F4F7", my: "5px" } }), _jsx(Box, { sx: { display: "flex", alignItems: "center", gap: "3px" }, children: down })] }));
const BillingSummaryTable = ({ yearMonth, rows, totals, hasFullAccess = true, onExport, exporting, onGenerateInvoice, onSaveDevelopmentHours, generatingInvoice, savingDevelopmentHours, canAdjustTotalBill = false, onSaveAdjustedAmounts, savingAdjustedAmounts, onLoadInvoiceMappings, onSaveInvoiceMappings, savingInvoiceMappings, onSaveInvoiceDetails, savingInvoiceDetails, }) => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState("client");
    const [sortOrder, setSortOrder] = useState("asc");
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchorEl, setColumnVisibilityAnchorEl] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [devHoursOpen, setDevHoursOpen] = useState(false);
    const [adjustBillOpen, setAdjustBillOpen] = useState(false);
    const [mapInvoiceOpen, setMapInvoiceOpen] = useState(false);
    const [detailsModalTarget, setDetailsModalTarget] = useState(null);
    /**
     * The invoice whose PDF is open, or null. An object rather than a boolean so the modal
     * gets the client name and invoice no. for its header alongside the id — and so a MAPPED
     * invoice can be opened as well as the row's own, which is what `readOnly` distinguishes.
     */
    const [previewTarget, setPreviewTarget] = useState(null);
    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q)
            return rows;
        return rows.filter((r) => r.client.toLowerCase().includes(q));
    }, [rows, search]);
    const sortedRows = useMemo(() => {
        const accessor = SORT_ACCESSORS[sortBy];
        if (!accessor)
            return filteredRows;
        return [...filteredRows].sort((a, b) => {
            const va = accessor(a);
            const vb = accessor(b);
            if (va < vb)
                return sortOrder === "asc" ? -1 : 1;
            if (va > vb)
                return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredRows, sortBy, sortOrder]);
    const totalPages = Math.max(1, Math.ceil(sortedRows.length / ITEMS_PER_PAGE));
    const pagedRows = useMemo(() => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return sortedRows.slice(start, start + ITEMS_PER_PAGE);
    }, [sortedRows, page]);
    // Searching can shrink the list past the current page — snap back to the first.
    const filteredCount = filteredRows.length;
    React.useEffect(() => {
        setPage(1);
    }, [filteredCount]);
    // ── Cells ─────────────────────────────────────────────────
    const renderCell = useCallback((row, col) => {
        switch (col.id) {
            case "invoiceNo":
                return _jsx(PlainText, { value: row.invoiceNo || "-" });
            // Client / AM / Source follow the Account Performance listing exactly.
            case "client":
                return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, px: 1, minWidth: 0 }, children: [hasFullAccess && row.hasMultipleInvoice && (_jsx(HoverTooltip, { arrow: true, placement: "top", title: 'Multiple Invoices generated for this client.', children: _jsx(Box, { component: "span", sx: { color: "#B42318", cursor: "help", lineHeight: 1 }, children: "*" }) })), _jsx(HoverTooltip, { title: row.client, arrow: true, placement: "top", children: _jsx(Typography, { sx: {
                                    fontWeight: 400, color: "#27272a", fontSize: "14px !important",
                                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                }, children: row.client }) }), row.isGroupClient && (_jsx(InfoTooltip, { title: _jsxs(Box, { sx: { minWidth: 140 }, children: [_jsx(Box, { sx: { px: 0.5, py: 0.25, fontSize: "13px" }, children: row.client }), row.groupClients.map((name) => (_jsx(Box, { sx: { px: 0.5, py: 0.25, fontSize: "13px" }, children: name }, name)))] }), placement: "top", size: 14, sx: { mt: "2px" } })), row.clientLinked && hasFullAccess &&
                            _jsx("img", { src: "/assets/link.svg", alt: "link", style: { cursor: "pointer" }, onClick: () => {
                                    const shouldOpenModal = isDualValue(row.source)
                                        ? [...row.source.up, ...row.source.down].includes("Tab")
                                        : row.source.includes("Tab");
                                    if (shouldOpenModal) {
                                        // Re-opens on what was saved for this client/month, and
                                        // falls back to the computed commission only when nothing
                                        // has been entered yet — an entered 0 is a real figure,
                                        // so it must not be replaced by the computed one.
                                        setDetailsModalTarget({
                                            rowId: row.id,
                                            clientName: row.client,
                                            totalCommissions: (row.commissionAmount ?? row.commissions).toFixed(2),
                                            invoiceSalesDescription: row.salesDescription,
                                        });
                                    }
                                    else {
                                        sessionStorage.setItem("commission_tracker_client_id", row.id);
                                        sessionStorage.setItem("commission_tracker_month", yearMonth);
                                        navigate(ROUTES.commissionTrackerReport.path, { state: { storeId: row.id, storeName: row.client, month: yearMonth } });
                                    }
                                } }), row.invoiceId && hasFullAccess && (_jsx(HoverTooltip, { title: "View invoice", arrow: true, placement: "top", children: _jsx(Box, { component: "span", onClick: (event) => {
                                    // The cell sits in a pinned column inside a clickable
                                    // row, so the click must not travel any further.
                                    event.stopPropagation();
                                    setPreviewTarget({
                                        invoiceId: row.invoiceId,
                                        invoiceNo: row.invoiceNo,
                                        clientName: row.client,
                                        readOnly: false,
                                    });
                                }, sx: {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    flexShrink: 0,
                                    cursor: "pointer",
                                    lineHeight: 1,
                                    "&:hover": { opacity: 0.75 },
                                }, children: _jsx("img", { src: "/assets/pdficon.svg", alt: "Invoice PDF", style: { width: 25, height: 25 } }) }) })), hasFullAccess && row.mappedInvoices.map((mapped) => (_jsx(HoverTooltip, { arrow: true, placement: "top", title: `View mapped invoice` +
                                `${mapped.invoiceDate ? ` (${mapped.invoiceDate})` : ""}`, children: _jsx(Box, { component: "span", onClick: (event) => {
                                    event.stopPropagation();
                                    setPreviewTarget({
                                        invoiceId: mapped.invoiceId,
                                        invoiceNo: mapped.invoiceNo,
                                        clientName: row.client,
                                        readOnly: true,
                                    });
                                }, sx: {
                                    display: "inline-flex",
                                    alignItems: "center",
                                    flexShrink: 0,
                                    cursor: "pointer",
                                    lineHeight: 1,
                                    "&:hover": { opacity: 0.75 },
                                }, children: _jsx("img", { src: "/assets/pdficon.svg", alt: "Mapped invoice PDF", style: { width: 25, height: 25 } }) }) }, mapped.invoiceId)))] }));
            case "am": {
                if (row.accountManagers.length === 0) {
                    return (_jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: _jsx(CommonChip, { label: "NA", tooltip: "Not Available", renderType: "circle", className: "circle manager-circle na" }) }));
                }
                const visible = row.accountManagers.slice(0, 2);
                const extra = row.accountManagers.slice(2);
                return (_jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: _jsxs(Box, { sx: { display: "flex", position: "relative", zIndex: 1 }, children: [visible.map((am, idx) => (_jsx(CommonChip, { label: am.initials, tooltip: am.name, renderType: "circle", index: idx, className: `circle manager-circle ${idx === 0 ? "first" : ""} ok` }, `${am.initials}-${idx}`))), extra.length > 0 && (_jsx(CommonChip, { label: `+${extra.length}`, renderType: "circle", index: 2, className: "circle extra-circle manager-circle", tooltip: _jsx(Box, { sx: { background: "black", borderRadius: "8px", overflow: "hidden", minWidth: 160 }, children: extra.map((am, i) => (_jsx(Box, { sx: {
                                            px: 1, py: 1, fontSize: "14px", textTransform: "capitalize",
                                            borderBottom: i !== extra.length - 1 ? "1px solid rgba(255,255,255,0.5)" : "none",
                                        }, children: am.name }, `${am.name}-${i}`))) }) }))] }) }));
            }
            // A `both` client can measure its sides on different networks, so Source
            // stacks two icon rows.
            case "source":
                return isDualValue(row.source)
                    ? (_jsx(DualStack, { align: "center", up: renderSourceIcons(row.source.up), down: renderSourceIcons(row.source.down) }))
                    : _jsx(Box, { sx: { display: "flex", justifyContent: "center" }, children: renderSourceIcons(row.source) });
            // case "internalStatus": return row.totalBill !== 0 ? <PlainText value={row.internalStatus || "-"} /> : '-';
            // A row billing nothing was never invoiced, so it has no state to report.
            case "invoiceStatus":
                return row.totalBill !== 0 && row.invoiceStatus
                    ? _jsx(InvoiceStatusCell, { row: row })
                    : _jsx(PlainText, { value: "-" });
            case "startDate": return _jsx(PlainText, { value: row.startDate || "-" });
            case "endDate": return _jsx(PlainText, { value: row.endDate || "-" });
            // Notes are free text of any length. This cell used to be `nowrap`, which
            // under `table-layout: auto` made the cell's min-content width the WHOLE
            // note on one line — so the column stretched to fit it and the ellipsis
            // never applied. Wrapping to a 4-line clamp lets the column hold its
            // declared width; the rest of the note stays available on hover.
            case "notes":
                if (!row.notes)
                    return _jsx(PlainText, { value: "-" });
                return (_jsx(HoverTooltip, { title: row.notes, arrow: true, placement: "left", children: _jsx(Box, { sx: { px: 1, cursor: "help" }, children: _jsx(Typography, { sx: {
                                fontFamily: "Inter", fontWeight: 400, color: "#27272a", fontSize: "14px !important",
                                whiteSpace: "normal",
                                lineHeight: "16px",
                                // Free text can contain an unbroken URL; break it rather
                                // than let one token widen the column again.
                                overflowWrap: "break-word",
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 4,
                                overflow: "hidden",
                            }, children: row.notes }) }) }));
            // ── Columns the API splits for `both` clients ──
            case "commDaysInMonth": return renderCount(row.commDaysInMonth);
            case "commDaysBilled": return renderCount(row.commDaysBilled);
            case "mgmtDaysInMonth": return renderCount(row.mgmtDaysInMonth);
            case "mgmtDaysBilled": return renderCount(row.mgmtDaysBilled);
            case "fullMgmtFee": return renderMoney(row.fullMgmtFee);
            case "billableMgmtFee": return renderMoney(row.billableMgmtFee);
            case "commissionBaseline": return renderMoney(row.commissionBaseline, true);
            case "revenueOrders": return renderMoney(row.revenueOrders);
            case "commissionableAmount": return renderMoney(row.commissionableAmount);
            case "commissionRate": {
                const rate = (value) => !value
                    ? _jsx(PlainText, { value: "-", noGutter: true })
                    : row.commissionRateIsCurrency
                        ? _jsx(CurrencyText, { value: value, noGutter: true })
                        : _jsx(PlainText, { value: `${value}%`, noGutter: true });
                return isDualValue(row.commissionRate)
                    ? _jsx(DualStack, { up: rate(row.commissionRate.up), down: rate(row.commissionRate.down) })
                    : _jsx(Box, { sx: { px: 1 }, children: rate(row.commissionRate) });
            }
            case "commissions": return _jsx(CurrencyText, { value: row.commissions });
            case "developmentHours": return _jsx(CurrencyText, { value: row.developmentHours });
            case "totalBill": return _jsx(CurrencyText, { value: row.totalBill });
            case "invoiceAmount": return _jsx(CurrencyText, { value: row.invoiceAmount });
            case "clientType": return _jsx(PlainText, { value: row.clientType });
            case "affiliatePer": return renderPercent(row.affiliatePer);
            case "paidSocialPer": return renderPercent(row.paidSocialPer);
            case "paidSearchPer": return renderPercent(row.paidSearchPer);
            case "accountManagementPer": return renderPercent(row.accountManagementPer);
            case "emailSmsPer": return renderPercent(row.emailSmsPer);
            case "totalPer": return renderPercent(row.totalPer);
            default: return null;
        }
    }, []);
    // ── Footer row: only the additive columns total ────────────
    const renderSummaryRow = useCallback((_rows, col) => {
        switch (col.id) {
            case "invoiceNo":
                return _jsx(Box, { sx: { px: 1 }, children: _jsx(Typography, { sx: { fontWeight: 700, color: "#181d27", fontSize: "14px !important" }, children: "Total" }) });
            case "fullMgmtFee": return _jsx(CurrencyText, { value: totals.fullMgmtFee, bold: true });
            case "billableMgmtFee": return _jsx(CurrencyText, { value: totals.billableMgmtFee, bold: true });
            case "revenueOrders": return _jsx(CurrencyText, { value: totals.revenueOrders, bold: true });
            case "commissionableAmount": return _jsx(CurrencyText, { value: totals.commissionableAmount, bold: true });
            case "commissions": return _jsx(CurrencyText, { value: totals.commissions, bold: true });
            case "developmentHours": return _jsx(CurrencyText, { value: totals.developmentHours, bold: true });
            case "totalBill": return _jsx(CurrencyText, { value: totals.totalBill, bold: true });
            case "invoiceAmount": return _jsx(CurrencyText, { value: totals.invoiceAmount, bold: true });
            default: return "";
        }
    }, [totals]);
    const selectedCount = selectedRows.size;
    const showSummaryRow = sortedRows.length > 0;
    // The single checked client, or null for a multi-row batch. Development hours
    // are always logged against one client.
    const singleSelectedId = selectedCount === 1 ? Array.from(selectedRows)[0] : null;
    const singleSelectedRow = rows.find((r) => r.id === singleSelectedId);
    const singleSelectedName = singleSelectedRow?.client;
    // Held open until the invoices are actually raised, so the Proceed button's loader
    // covers the QuickBooks round-trip instead of the dialog vanishing into dead air.
    const handleProceed = useCallback(async () => {
        const raised = await onGenerateInvoice?.(Array.from(selectedRows));
        if (raised !== false)
            setConfirmOpen(false);
    }, [onGenerateInvoice, selectedRows]);
    // Hand off from the confirmation to the hours repeater — the first dialog closes
    // so only one is ever on screen.
    const handleAddDevelopmentHours = useCallback(() => {
        setConfirmOpen(false);
        setDevHoursOpen(true);
    }, []);
    // Only dismiss once the save has actually landed — a rejected request returns
    // `false`, and the repeater stays open with the typed lines intact.
    const handleSaveDevelopmentHours = useCallback(async (entries) => {
        if (!singleSelectedId)
            return;
        const saved = await onSaveDevelopmentHours?.(singleSelectedId, entries);
        if (saved !== false)
            setDevHoursOpen(false);
    }, [onSaveDevelopmentHours, singleSelectedId]);
    // "Generate Invoice" from inside the repeater bills the entered lines too — the page
    // saves them first, since the endpoint recomputes the amounts from what's stored.
    const handleGenerateWithDevelopmentHours = useCallback(async (entries) => {
        if (!singleSelectedId)
            return;
        const raised = await onGenerateInvoice?.([singleSelectedId], entries);
        if (raised !== false)
            setDevHoursOpen(false);
    }, [onGenerateInvoice, singleSelectedId]);
    const handleSaveInvoiceDetails = useCallback(async (totalCommissions, invoiceSalesDescription) => {
        if (!detailsModalTarget)
            return;
        const saved = await onSaveInvoiceDetails?.(detailsModalTarget.rowId, totalCommissions, invoiceSalesDescription);
        if (saved !== false) {
            setDetailsModalTarget(null);
        }
    }, [detailsModalTarget, onSaveInvoiceDetails]);
    /**
     * The checked clients, seeded with the Total Bill each one currently shows.
     *
     * Taken from `rows` rather than `sortedRows` on purpose: a checked row stays checked
     * when the search box narrows the listing, so the modal has to be able to reach
     * clients that aren't on screen at the moment. `toFixed(2)` seeds the input at the
     * precision the endpoint stores.
     */
    const adjustBillEntries = useMemo(() => rows
        .filter((row) => selectedRows.has(row.id))
        .map((row) => ({
        id: row.id,
        client: row.client,
        invoiceAmount: row.invoiceAmount,
        totalBill: row.totalBill.toFixed(2),
    })), [rows, selectedRows]);
    // Only dismiss once the save has landed — a rejected request returns `false` and the
    // modal stays open showing why, with the typed amounts intact.
    const handleSubmitAdjustedAmounts = useCallback(async (entries) => {
        const saved = await onSaveAdjustedAmounts?.(entries);
        if (saved === false)
            return false;
        setAdjustBillOpen(false);
        return true;
    }, [onSaveAdjustedAmounts]);
    /**
     * The checked clients that QuickBooks holds more than one invoice for.
     *
     * Only these are worth sending to the mapping modal — a client with a single invoice has
     * no extras to list. Taken from `rows` rather than `sortedRows` for the same reason the
     * Adjust Total Bill entries are: a checked row stays checked when the search box narrows
     * the listing, so it may not be on screen.
     */
    const multipleInvoiceClientIds = useMemo(() => rows.filter((row) => selectedRows.has(row.id) && row.hasMultipleInvoice).map((row) => row.id), [rows, selectedRows]);
    // Same rule as above: `false` keeps the modal open with the ticks intact.
    const handleSubmitInvoiceMappings = useCallback(async (entries) => {
        const saved = await onSaveInvoiceMappings?.(entries);
        if (saved === false)
            return false;
        setMapInvoiceOpen(false);
        return true;
    }, [onSaveInvoiceMappings]);
    return (_jsxs(Box, { sx: { bgcolor: "#fff", border: "1px solid #D0D5DD", borderRadius: "8px", overflow: "hidden" }, children: [_jsx(TableToolbar, { title: "", showSearch: true, searchValue: search, onSearchChange: setSearch, searchPlaceholder: "Search client...", showRows: true, enableColumnVisibility: true, onColumnVisibilityClick: (e) => setColumnVisibilityAnchorEl(e.currentTarget), showFullScreen: true, customActions: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [hasFullAccess && selectedCount > 0 && canAdjustTotalBill && (_jsx(AppButton, { variant: "contained", color: "inherit", loading: savingAdjustedAmounts, onClick: () => setAdjustBillOpen(true), sx: {
                                height: "36px", px: 2, borderRadius: "8px",
                                textTransform: "none", fontWeight: 500, fontSize: "13px",
                                whiteSpace: "nowrap",
                                bgcolor: "#181D27", color: "#fff", border: "1px solid #181D27",
                                "&:hover": { bgcolor: "#101828", border: "1px solid #101828" },
                            }, className: "all-Font-Sizes-12", children: "Update Total Bill" })), hasFullAccess && multipleInvoiceClientIds.length > 0 && (_jsx(AppButton, { variant: "contained", color: "inherit", loading: savingInvoiceMappings, onClick: () => setMapInvoiceOpen(true), sx: {
                                height: "36px", px: 2, borderRadius: "8px",
                                textTransform: "none", fontWeight: 500, fontSize: "13px",
                                whiteSpace: "nowrap",
                                bgcolor: "#181D27", color: "#fff", border: "1px solid #181D27",
                                "&:hover": { bgcolor: "#101828", border: "1px solid #101828" },
                                gap: 1
                            }, className: "all-Font-Sizes-12", children: "Mapping Invoice" })), hasFullAccess && selectedCount > 0 && (_jsxs(AppButton, { variant: "contained", loading: generatingInvoice, onClick: () => setConfirmOpen(true), sx: {
                                height: "36px", px: 2, borderRadius: "8px",
                                textTransform: "none", fontWeight: 500, fontSize: "13px",
                                whiteSpace: "nowrap",
                                bgcolor: "#FF008E", border: "1px solid #FF008E",
                                "&:hover": { bgcolor: "#e0007c" },
                                gap: 1
                            }, className: "all-Font-Sizes-12", children: ["Generate Invoice (", selectedCount, ")"] })), hasFullAccess && rows.length > 0 && _jsx(ExportButton, { onClick: () => onExport?.(), loading: exporting })] }) }), _jsx(Box, { className: "table-container", sx: { ...tableContainerSx, position: "relative", borderRadius: 0, border: "none", borderTop: "1px solid #EAECF0" }, children: _jsx(CommonTable, { columns: columns, rows: sortedRows, renderCell: renderCell, sortConfig: { columnId: sortBy, direction: sortOrder }, onSortChange: (id, dir) => { setSortBy(id); setSortOrder(dir || (sortOrder === "asc" ? "desc" : "asc")); }, expandTrigger: EXPAND_TRIGGERS.NONE, enableSelection: hasFullAccess, selectedRows: selectedRows, onSelectionChange: setSelectedRows, 
                    // Nothing to invoice on a zero/negative bill, so those rows aren't
                    // selectable — this also keeps select-all and the header's
                    // indeterminate state scoped to the billable rows.
                    isRowSelectable: isRowSelectable, 
                    // `isRowSelectable` alone only DISABLES the checkbox; hide it outright
                    // so the column reads as "not applicable" rather than "blocked".
                    rowSx: hideCheckboxWhenNotBillable, enableColumnActions: false, enableColumnReorder: false, enableColumnPinning: true, pinnedColumns: PINNED_COLUMNS, enableColumnFilters: false, enableColumnVisibility: false, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchorEl: columnVisibilityAnchorEl, onColumnVisibilityClose: () => setColumnVisibilityAnchorEl(null), columnVisibilityConfig: { enable: true, columns: HIDEABLE_COLUMN_IDS, enableReorder: false }, enableSummaryRow: showSummaryRow, renderSummaryRow: showSummaryRow ? renderSummaryRow : undefined, summaryRowPosition: SUMMARY_ROW_POSITIONS.BOTTOM, isLoading: false, 
                    // Swaps the label's class so App.css's `nowrap` on
                    // `.reusable-table-header-label` never applies — see the
                    // `.billing-header-label` block in `tableContainerSx`.
                    tableHeadClassName: "billing-header-label", stickyHeader: true, maxHeight: "calc(100vh - 220px)", emptyMessage: "No billing data available for this month." }) }), _jsx(Box, { className: "user-list-pagination-wrapper", children: _jsx(Pagination, { currentPage: 1, totalPages: 1, onPageChange: setPage, maxVisiblePages: 5, totalEntries: sortedRows.length, itemsPerPage: ITEMS_PER_PAGE }) }), _jsx(GenerateInvoiceModal, { open: confirmOpen, selectedCount: selectedCount, onProceed: handleProceed, onAddDevelopmentHours: handleAddDevelopmentHours, onClose: () => setConfirmOpen(false), loading: generatingInvoice }), _jsx(InvoicePreviewModal, { open: previewTarget !== null, invoiceId: previewTarget?.invoiceId || "", invoiceNo: previewTarget?.invoiceNo, clientName: previewTarget?.clientName, onClose: () => setPreviewTarget(null), 
                // A mapped extra has no review state to read or write — see the prop's note.
                // readOnly={previewTarget?.readOnly ?? false}
                readOnly: true }), _jsx(BillingSummaryDetailsModal, { open: detailsModalTarget !== null, clientName: detailsModalTarget?.clientName || "", initialTotalCommissions: detailsModalTarget?.totalCommissions || "", initialInvoiceSalesDescription: detailsModalTarget?.invoiceSalesDescription || "", saving: savingInvoiceDetails, onSave: handleSaveInvoiceDetails, onClose: () => setDetailsModalTarget(null) }), multipleInvoiceClientIds.length > 0 && onLoadInvoiceMappings && (_jsx(MappingInvoiceModal, { open: mapInvoiceOpen, clientIds: multipleInvoiceClientIds, onLoad: onLoadInvoiceMappings, onSubmit: handleSubmitInvoiceMappings, onClose: () => setMapInvoiceOpen(false), loading: savingInvoiceMappings })), _jsx(AdjustTotalBillModal, { open: adjustBillOpen, entries: adjustBillEntries, onSubmit: handleSubmitAdjustedAmounts, onClose: () => setAdjustBillOpen(false), loading: savingAdjustedAmounts }), _jsx(DevelopmentHoursModal, { open: devHoursOpen, clientName: singleSelectedName, 
                // Whatever is already saved against this client for the month, so the
                // repeater edits the existing lines instead of starting from blank.
                initialEntries: singleSelectedRow?.developmentHoursDetail, onSave: handleSaveDevelopmentHours, onGenerateInvoice: handleGenerateWithDevelopmentHours, onClose: () => setDevHoursOpen(false), loading: savingDevelopmentHours || generatingInvoice })] }));
};
export default BillingSummaryTable;
